import { Box, Typography } from '@mui/material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Logo from './Logo';
import navItems from '../data/navigation';
import { COMPANY_EMAIL, COMPANY_LEGAL, COMPANY_NAME, COMPANY_PHONE, mailHref, phoneHref } from '../data/company';
import { DUR, EASE_CSS, FONT, LAYOUT, TYPE } from '../theme/tokens';

// Capul unei coloane din footer.
const columnTitle = {
  fontFamily: FONT.mono,
  fontSize: 11.5,
  fontWeight: 500,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: 'var(--qf-ink-3)',
  mb: 2,
};

// Rândurile sunt linkuri: 40px înălțime, ca degetul să nu rateze între ele.
const rowLink = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  minHeight: 40,
  ...TYPE.small,
  color: 'var(--qf-ink-2)',
  textDecoration: 'none',
  transition: `color ${DUR.fast}s ${EASE_CSS}`,
  '@media (hover: hover)': { '&:hover': { color: 'var(--qf-ink)' } },
  '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 3, borderRadius: '6px' },
};

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'var(--qf-bg-alt)',
        borderTop: '1px solid var(--qf-line)',
        color: 'var(--qf-ink)',
        pt: { xs: 6, md: 8 },
        // Bara de gesturi de pe iPhone stătea peste ultimul rând.
        pb: 'max(32px, calc(env(safe-area-inset-bottom) + 20px))',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: LAYOUT.maxWidth,
          mx: 'auto',
          paddingInline: LAYOUT.gutter,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.7fr 1fr 1.2fr' },
            gap: { xs: 5, md: 6 },
          }}
        >
          {/* ---- Marca ---- */}
          <Box sx={{ gridColumn: { sm: '1 / -1', md: 'auto' }, maxWidth: 380 }}>
            <Box
              component={Link}
              to="/"
              aria-label={`${COMPANY_NAME} — acasă`}
              sx={{
                display: 'inline-flex',
                color: 'var(--qf-ink)',
                textDecoration: 'none',
                transition: `opacity ${DUR.fast}s ${EASE_CSS}`,
                '@media (hover: hover)': { '&:hover': { opacity: 0.7 } },
                '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 4, borderRadius: '8px' },
              }}
            >
              <Logo size={32} nameSx={{ fontSize: 20 }} />
            </Box>

            <Typography sx={{ ...TYPE.small, color: 'var(--qf-ink-2)', mt: 2, maxWidth: '38ch', textWrap: 'pretty' }}>
              {t('footer.tagline')}
            </Typography>
          </Box>

          {/* ---- Navigare ---- */}
          <Box component="nav" aria-label={t('footer.navTitle')}>
            <Typography component="h2" sx={columnTitle}>
              {t('footer.navTitle')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {navItems.map((item) => (
                <Box key={item.key} component={Link} to={item.path} sx={rowLink}>
                  {t(item.key)}
                </Box>
              ))}
            </Box>
          </Box>

          {/* ---- Contact ---- */}
          <Box>
            <Typography component="h2" sx={columnTitle}>
              {t('footer.contactTitle')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box component="a" href={mailHref} sx={{ ...rowLink, overflowWrap: 'anywhere' }}>
                <MailOutlineRoundedIcon sx={{ fontSize: 18, color: 'var(--qf-ink-3)', flex: 'none' }} />
                {COMPANY_EMAIL}
              </Box>

              <Box component="a" href={phoneHref} sx={rowLink}>
                <CallOutlinedIcon sx={{ fontSize: 18, color: 'var(--qf-ink-3)', flex: 'none' }} />
                {COMPANY_PHONE}
              </Box>

              <Box sx={{ ...rowLink, color: 'var(--qf-ink-3)', pointerEvents: 'none' }}>
                <PlaceOutlinedIcon sx={{ fontSize: 18, flex: 'none' }} />
                {t('footer.location')}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ---- Rândul de subsol ---- */}
        <Box
          sx={{
            mt: { xs: 5, md: 7 },
            pt: 3,
            borderTop: '1px solid var(--qf-line)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.5,
          }}
        >
          <Typography sx={{ ...TYPE.caption, color: 'var(--qf-ink-3)' }}>
            © {year} {COMPANY_NAME} · {COMPANY_LEGAL}. {t('footer.rights')}
          </Typography>

          <Typography sx={{ ...TYPE.caption, color: 'var(--qf-ink-3)' }}>
            {t('footer.builtIn')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
